import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { AppRole } from "@/lib/nav";

interface UserRolesState {
  roles: AppRole[];
  primaryRole: AppRole;
  isAdmin: boolean;
  isInstructor: boolean;
  isParent: boolean;
  loading: boolean;
}

/**
 * Loads which roles the current user has and picks a "primary" role
 * for sidebar selection. Order of precedence: admin > instructor > parent > student.
 */
export function useUserRoles(): UserRolesState {
  const { user } = useAuth();
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user) {
        if (!cancelled) {
          // Guests get the "student" sidebar (learn items are open;
          // auth-gated items prompt sign-up on click).
          setRoles(["student"]);
          setLoading(false);
        }
        return;
      }
      setLoading(true);
      try {
        const checks = ["admin", "instructor", "parent"] as const;
        const results = await Promise.all(
          checks.map((role) =>
            supabase.rpc("has_role", { _user_id: user.id, _role: role })
          )
        );
        const found: AppRole[] = [];
        results.forEach((r, i) => {
          if (!r.error && r.data) found.push(checks[i]);
        });
        // Every authenticated user is at least a student.
        found.push("student");
        if (!cancelled) setRoles(found);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const isAdmin = roles.includes("admin");
  const isInstructor = roles.includes("instructor");
  const isParent = roles.includes("parent");

  const primaryRole: AppRole = isAdmin
    ? "admin"
    : isInstructor
    ? "instructor"
    : isParent
    ? "parent"
    : "student";

  return { roles, primaryRole, isAdmin, isInstructor, isParent, loading };
}
